import { YAHOO_TARGET_FIELDS } from './yahoo/yahooTargeting';
import YahooTargetingPicker, { YAHOO_LOOKUP_FIELDS } from './yahoo/YahooTargetingPicker';
/**
 * Cell detail panel — the slide-over that opens when a reviewer clicks any
 * cell in the review grid. Owns the per-cell hero (scalar value with inline
 * editor, or a bulleted list with per-item delete for audiences / keywords /
 * interests / PMP deals / geo include+exclude), the cell's validation
 * annotation, row context, and the expandable full-row dump.
 *
 * Extracted from CampaignLaunchWorkflow.js so LaunchCampaignDetails (the
 * Launch-board campaign details page) renders the exact same panel — the
 * audiences modal, geo modal, and every other cell modal are this one
 * component. All edit state (editedRows / editingCell / pendingDeletions)
 * stays owned by the parent so both hosts share one commit/cancel path.
 *
 * Styling lives in CampaignLaunchWorkflow.css under the global .clw-*
 * classes, which read CSS variables declared on the `.clw` root — any host
 * must render this inside an element carrying the `clw` class.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue, TYPEAHEAD_DEBOUNCE_MS } from '../../hooks/useDebouncedValue';
import { fmtCellValue } from './clwFormatters';
import { decodeLookupValue, encodeLookupValue, encodeTypedLookupValue, lookupValueLabel, numeric } from './sharedFieldWriters';
import {
  TTD_SCALED_FIELDS, DV360_SCALED_FIELDS, fmtScaledValue, pluralUnits,
} from './clwGridConstants';
import { GADS_SCALED_FIELDS } from './gads/gadsConstants';
import { FB_SCALED_FIELDS, FB_PLATFORM_LABEL } from './fb/fbConstants';
import { GT_TAXONOMY_TYPE_LABELS } from './groundtruth/gtFieldDescriptor';

// How long to sit on a keystroke before firing a lookup query. Each query is a
// live platform round trip, so this is deliberately generous. The value is the
// shared app-wide typeahead debounce — every search-as-you-type fetch keys off
// the same hook/constant (see src/hooks/useDebouncedValue.js).
export const LOOKUP_DEBOUNCE_MS = TYPEAHEAD_DEBOUNCE_MS;

// ─── PARENT-SCALED WRITE PREVIEW ─────────────────────────────
// Three cells (budget / imps / dailyCap) display a PER-ROW SHARE of a value
// stored on the PARENT entity, and committing one writes the scaled-up total.
// Because the cell re-renders the share afterwards, the number the user
// actually set was never shown anywhere: typing 50000 into one row of a
// 20-ad-group TTD campaign sets a $1,000,000 campaign budget and the cell still
// reads $50,000.
//
// So the editor states the total BEFORE the commit, live as the user types.
// This is DISCLOSURE, NOT A GATE — it never blocks the edit.
//
// The per-platform configs (TTD_SCALED_FIELDS / DV360_SCALED_FIELDS /
// GADS_SCALED_FIELDS / FB_SCALED_FIELDS) compute `total` through the SAME
// helpers the appliers write through (scaleToParent / dv360ImpsFromBudget in
// sharedFieldWriters), which is the point of routing this through config: a
// preview that disagrees with what is written would be worse than none.
//
// Keyed by `row.channel`, which each platform's row builder stamps.
const SCALED_FIELDS_BY_CHANNEL = {
  TTD: TTD_SCALED_FIELDS,
  DV360: DV360_SCALED_FIELDS,
  'Google Ads': GADS_SCALED_FIELDS,
  // Facebook's only scaled cell is `campaignBudget` — an ad set's own budget
  // is stored on the ad set, so it writes exactly what is typed.
  [FB_PLATFORM_LABEL]: FB_SCALED_FIELDS,
};

/**
 * The disclosure line for a scaled cell mid-edit, or null when there is
 * nothing truthful to say: an unscaled field, an unknown platform, a draft
 * `numeric` can't parse (post-hardening that is null, and the applier would
 * no-op too), or a total the config can't derive (DV360 budget with no CPM —
 * again exactly when the write no-ops).
 *
 * Exported for tests: this string is the contract, so it is asserted directly
 * rather than through the rendered panel.
 */
export function scaledWritePreview({ row, field, draft }) {
  const cfg = SCALED_FIELDS_BY_CHANNEL[row?.channel]?.[field];
  if (!cfg) return null;
  const share = numeric(draft);
  if (share == null) return null;
  const total = cfg.total(share, row);
  if (total == null || !Number.isFinite(total)) return null;

  const detail = cfg.detail
    ? cfg.detail(share, row)
    // Default tail: the plain "share × N siblings" multiply, omitted for a
    // single-row parent where share === total and there is nothing to explain.
    : (() => {
      const n = Number(row?.siblingCount);
      if (!Number.isFinite(n) || n <= 1) return null;
      const noun = typeof cfg.sibNoun === 'function' ? cfg.sibNoun(row) : cfg.sibNoun;
      return `${fmtScaledValue(share, cfg.unit)} × ${pluralUnits(n, noun)}`;
    })();

  const headline = `Sets ${cfg.scope} to ${fmtScaledValue(total, cfg.unit)}`;
  return detail ? `${headline} (${detail})` : headline;
}

// ─── INLINE SCALAR EDITOR ────────────────────────────────────
// Renders either the read-only value + Edit pencil, or an inline input
// + Save/Cancel controls when this cell is the active editingCell. Lives
// inside CellDetailPanel's hero block; the heavy commit/cancel logic is
// up in the parent component so all platforms share the editedRows state.
// The editor only shows for fields in editableFields — anything not in
// the platform's round-trip allow-list stays read-only so users never
// see a "saved" state for a value that wouldn't actually persist.
export function CellScalarEditor({
  detailRow, detailCellField, heroVal, rawValue, isMonoVal,
  alreadyLaunched = false, editableFields, fieldEditors,
  editingCell, setEditingCell, editingDraft, setEditingDraft,
  commitCellEdit, cancelCellEdit, editedRows,
  // Fixed option sets for 'select' editors ({ field: [options] }). Optional —
  // hosts without one keep every field on the free-text/number/date inputs.
  fieldOptions = null,
  // Why each read-only field is read-only ({ field: reason }). Optional —
  // shown under the value so a missing Edit button reads as a decision, not
  // an omission. GT already keeps this discipline (gtReadOnlyReasons); this
  // extends it to every platform.
  readOnlyReasons = null, onLookup = null,
}) {
  const canEdit = !alreadyLaunched
    && editableFields?.has(detailCellField)
    && detailRow != null;
  const isEditing = canEdit
    && editingCell?.rowId === detailRow.rowId
    && editingCell?.field === detailCellField;
  const isEdited = canEdit
    && editedRows?.[detailRow.rowId]
    && Object.prototype.hasOwnProperty.call(editedRows[detailRow.rowId], detailCellField);
  const editorType = fieldEditors?.[detailCellField] || 'text';
  const isPlannedUnits = detailCellField === 'impression_clicks';
  const plannedCount = isPlannedUnits ? numeric(editingDraft) : null;
  const invalidPlannedUnits = isPlannedUnits && (!Number.isSafeInteger(plannedCount) || plannedCount < 0);
  const startEdit = () => {
    if (!canEdit) return;
    // Seed the draft from the current effective value (raw, not formatted)
    // so the user edits the underlying value rather than the display string.
    // Currency fields strip the leading $ so the numeric prefix shows in the
    // input; date fields keep YYYY-MM-DD which the native input accepts.
    // Numeric editors seed with the value's NUMBER, not its display string:
    // several cells render a unit alongside it ('$6.50 CPA', '70%'), and
    // `numeric` deliberately rejects a draft carrying a suffix — so seeding the
    // display string would make an untouched Save silently no-op.
    let seed = rawValue;
    if (editorType === 'currency' || editorType === 'number') {
      const m = /-?[\d,]+(?:\.\d+)?/.exec(String(rawValue ?? ''));
      seed = m ? (isPlannedUnits ? m[0].replace(/,/g, '') : m[0]) : (rawValue ?? '');
    } else if (rawValue == null) seed = '';
    setEditingDraft(String(seed ?? ''));
    setEditingCell({ rowId: detailRow.rowId, field: detailCellField });
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); if (!invalidPlannedUnits) commitCellEdit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelCellEdit(); }
  };

  if (isEditing) {
    const inputType = isPlannedUnits ? 'number' : editorType === 'date' ? 'date'
      : (editorType === 'number' || editorType === 'currency') ? 'text'
      : 'text';
    // Fixed-option cells render a select instead of the free-text input. The
    // stored value stays visible even when it isn't in the option set —
    // rendering must never silently rewrite the config; only a pick does.
    const selectOptions = editorType === 'select'
      ? (fieldOptions?.[detailCellField] || null)
      : null;
    // API values can be lowercase (`click`), while the existing option is
    // `Click`. Select that option without changing the stored draft on render.
    const matchedOption = selectOptions?.find((option) => (
      String(option).trim().toLowerCase() === String(editingDraft ?? '').trim().toLowerCase()
    ));
    // What the parent entity will actually be set to, for the cells that show
    // a share of it. Recomputed on every keystroke; null (→ nothing rendered)
    // for unscaled fields and for drafts that wouldn't be written anyway.
    const scaledPreview = scaledWritePreview({
      row: detailRow, field: detailCellField, draft: editingDraft,
    });
    return (
      <div className="clw-cell-detail-edit">
        {selectOptions ? (
          <select
            autoFocus
            className={`clw-cell-detail-edit-input${isMonoVal ? ' mono' : ''}`}
            value={matchedOption ?? editingDraft}
            onChange={(e) => setEditingDraft(e.target.value)}
            onKeyDown={onKeyDown}
            name={`clw-edit-${detailRow?.rowId || ''}-${detailCellField}`}
          >
            {matchedOption === undefined && (
              <option value={editingDraft}>{editingDraft === '' ? '—' : editingDraft}</option>
            )}
            {selectOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
        <input
          autoFocus
          type={inputType}
          min={isPlannedUnits ? 0 : undefined}
          step={isPlannedUnits ? 1 : undefined}
          aria-invalid={isPlannedUnits ? invalidPlannedUnits : undefined}
          className={`clw-cell-detail-edit-input${isMonoVal ? ' mono' : ''}`}
          value={editingDraft}
          onChange={(e) => setEditingDraft(e.target.value)}
          onKeyDown={onKeyDown}
          // Suppress browser autofill / password-save icons that would
          // otherwise dock to the right of the input.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          name={`clw-edit-${detailRow?.rowId || ''}-${detailCellField}`}
        />
        )}
        {detailRow?.yahooLineId && YAHOO_TARGET_FIELDS[detailCellField] && !YAHOO_LOOKUP_FIELDS[detailCellField] && (
          <p>Enter a comma-separated list. Saving replaces this line’s list across all its flights; other lines keep their targeting.</p>
        )}
        {detailRow?.yahooLineId && YAHOO_LOOKUP_FIELDS[detailCellField] && onLookup && (
          <YahooTargetingPicker key={`${detailRow.rowId}-${detailCellField}`} field={detailCellField} row={detailRow}
            onLookup={onLookup} value={editingDraft} onChange={setEditingDraft} />
        )}
        {scaledPreview && (
          <div className="clw-cell-detail-edit-preview">{scaledPreview}</div>
        )}
        {invalidPlannedUnits && <p role="alert">Enter a whole number of zero or greater.</p>}
        <div className="clw-cell-detail-edit-actions">
          <button type="button" className="clw-btn clw-btn-primary clw-btn-sm" onClick={commitCellEdit} disabled={invalidPlannedUnits}>Save</button>
          <button type="button" className="clw-btn clw-btn-secondary clw-btn-sm" onClick={cancelCellEdit}>Cancel</button>
          <span className="clw-cell-detail-edit-hint">Enter · Esc</span>
        </div>
      </div>
    );
  }

  // The reason this field is read-only, when the FIELD is the reason (not the
  // launch state) and the host supplied one.
  const readOnlyReason = !alreadyLaunched
    && detailRow != null
    && editableFields
    && !editableFields.has(detailCellField)
    ? readOnlyReasons?.[detailCellField] || null
    : null;

  return (
    <>
      <div className={`clw-cell-detail-val${isMonoVal ? ' mono' : ''}${isEdited ? ' is-edited' : ''}`}>
        {heroVal}
      </div>
      {canEdit && (
        <button
          type="button"
          className="clw-cell-detail-edit-btn"
          onClick={startEdit}
          title={isEdited ? 'Edit again' : 'Edit this value'}
        >
          <span className="clw-cell-detail-edit-btn-icon" aria-hidden="true">✎</span>
          <span>{isEdited ? 'Edit again' : 'Edit'}</span>
        </button>
      )}
      {readOnlyReason && (
        <div className="clw-cell-detail-list-note">Read-only: {readOnlyReason}</div>
      )}
    </>
  );
}

// ─── EDITABLE LIST ───────────────────────────────────────────
// One list-shaped cell section (audiences / keywords / interests / pmp, or
// one of the geo include/exclude lists) with per-item remove, per-item edit
// (when the platform's list config grants it), and an add row at the foot.
// Staged ops come from the parent's pendingListOps (via pendingOps); removes
// keep riding the legacy pendingDeletionsForRow view so the strikethrough
// rendering works even for hosts that don't pass the new props yet.
export function EditableList({
  rowId, field, items, cfg, canRemove, pendingRemoved, pendingOps,
  onToggleListDeletion, onAddListItem, onEditListItem, removeTitle, onLookup,
  onRefreshLookup,
  row,
}) {
  // Which existing item label is being inline-edited, and its draft. A
  // lookup-backed field never free-text edits: its ✎ reopens the picker and
  // the commit REPLACES the picked record (the id travels with the new pick).
  const [editingItem, setEditingItem] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [addDraft, setAddDraft] = useState('');
  const [replacingItem, setReplacingItem] = useState(null);
  const [replaceDraft, setReplaceDraft] = useState('');

  const needsLookup = Boolean(cfg?.lookup?.kind);
  const editViaLookup = Boolean(cfg?.editViaLookup && needsLookup);
  const canEdit = Boolean(cfg?.ops?.edit && onEditListItem && (!editViaLookup || onLookup));
  // A lookup-backed field is only addable when the host actually supplied a
  // resolver — without one there is no way to obtain the platform id, and a
  // free-text box would stage a value the launcher can't use. Single-select
  // cells additionally need an empty slot before Add shows.
  const single = Boolean(cfg?.single);
  const stagedAdds = (Array.isArray(pendingOps) ? pendingOps : [])
    .filter((o) => o.op === 'add')
    .map((o) => o.value);
  const canAdd = Boolean(cfg?.ops?.add && onAddListItem
    && (!needsLookup || onLookup)
    && (!single || (items.length + stagedAdds.length) === 0));
  const removable = Boolean(canRemove && onToggleListDeletion);
  const ops = Array.isArray(pendingOps) ? pendingOps : [];
  const editByValue = new Map(ops.filter((o) => o.op === 'edit').map((o) => [o.value, o.newValue]));

  // Lookup-backed adds: the typed text is a SEARCH TERM, not the value. The
  // value only exists once the user picks a result, because it has to carry
  // the platform id (see encodeLookupValue / encodeTypedLookupValue).
  const lookup = canAdd && needsLookup ? cfg.lookup : null;
  const [lookupState, setLookupState] = useState({ status: 'idle', rows: [], error: null });
  const [replaceState, setReplaceState] = useState({ status: 'idle', rows: [], error: null });

  // Debounced query. Two refs at work:
  //  - resolverRef holds the latest onLookup so a host that re-creates the
  //    callback each render can't reset the debounce timer on every render
  //    (which would stop the query ever firing);
  //  - seqRef discards a slow response for an earlier keystroke that lands
  //    after a newer one, so the dropdown never shows stale results.
  // The third argument is the row/field context — GT Drive-To uses it to
  // choose the taxonomy type filter for the picker.
  const resolverRef = useRef(onLookup);
  resolverRef.current = onLookup;
  // Row context can be re-created per render; keep the latest one without
  // resetting the debounce timer on identity churn.
  const rowRef = useRef(row);
  rowRef.current = row;
  const lookupSeq = useRef(0);
  const lookupKind = lookup?.kind || null;

  // On-demand GT taxonomy cache warmup: campaigns that were never processed
  // through the launcher have no cached taxonomy file, so the search 404s
  // with "No GT taxonomy cache …". The host may supply onRefreshLookup (an
  // app-backend call that makes campaign-services fetch the taxonomy live and
  // cache it to GCS); this button runs it and re-fires the current search
  // through the normal debounced path when it succeeds.
  // The FIELD's lookup kind, independent of canAdd: a populated single-select
  // (Drive-To, Location group) has canAdd=false, but its ✎ replace picker is
  // still a GT taxonomy search — the refresh button and the friendly error
  // wording must apply there too.
  const fieldLookupKind = cfg?.lookup?.kind || null;
  const lookupRefreshable = fieldLookupKind === 'gt-taxonomy' && typeof onRefreshLookup === 'function';

  // Lookup backends return operator-grade messages — raw account keys (which
  // can be a pasted URL), axios status codes, service names. Reviewers get
  // plain language (UAT 0818); the raw text still lands in the console below
  // for debugging.
  const friendlyLookupError = (msg) => {
    const raw = String(msg || '');
    if (/No GT taxonomy cache/i.test(raw)) {
      return "This account's audience taxonomy isn't loaded yet — fetch it below and the search will re-run.";
    }
    // GT wording only on GT pickers — this render site is shared with the
    // TTD geo/audience lookups, whose transport errors must not tell the
    // user to go check a GroundTruth account id.
    if (fieldLookupKind === 'gt-taxonomy'
        && /status code|Failed to load GT taxonomy|ECONN|ETIMEDOUT|Network Error/i.test(raw)) {
      return "Couldn't reach GroundTruth to load the taxonomy — try again in a moment. If it keeps failing, check that this campaign's platform account ID is the numeric GroundTruth account ID.";
    }
    return raw;
  };

  const [refreshState, setRefreshState] = useState({ busy: false, error: null });
  const [retryTick, setRetryTick] = useState(0);
  const runLookupRefresh = async () => {
    if (!lookupRefreshable || refreshState.busy) return;
    setRefreshState({ busy: true, error: null });
    try {
      await onRefreshLookup();
      setRefreshState({ busy: false, error: null });
      setRetryTick((t) => t + 1);
    } catch (e) {
      // Raw error to the console for debugging; plain language on screen.
      console.warn('[gt-taxonomy] fetch failed:', e?.message || e);
      setRefreshState({ busy: false, error: friendlyLookupError(e?.message || 'Fetch failed') });
    }
  };

  // GT taxonomy pickers are TYPE-SCOPED: the reviewer picks the audience type
  // first and the search filters to it. The merged audiences cell spans every
  // taxonomy type, so an unfiltered typeahead mixes providers and id spaces.
  // Options come from the field's descriptor lookup.types; an empty list means
  // "all types" and expands to the full labeled set.
  const gtTypeOptions = useMemo(() => {
    if (lookupKind !== 'gt-taxonomy') return [];
    const allowed = Array.isArray(cfg?.lookup?.types) && cfg.lookup.types.length > 0
      ? cfg.lookup.types
      : Object.keys(GT_TAXONOMY_TYPE_LABELS);
    return allowed.map((t) => ({ value: t, label: GT_TAXONOMY_TYPE_LABELS[t] || t }));
  }, [lookupKind, cfg]);
  const defaultGtType = useMemo(() => {
    if (gtTypeOptions.length === 0) return null;
    if (gtTypeOptions.length === 1) return gtTypeOptions[0].value;
    // Drive-To is the dynamic case: the row's drive_to_type picks brand vs
    // location group; everything else starts on the first option.
    if (field === 'tracking_visits_to'
        && String(row?.drive_to_type || '').trim().toLowerCase() === 'brand'
        && gtTypeOptions.some((o) => o.value === 'brand')) return 'brand';
    return gtTypeOptions[0].value;
  }, [gtTypeOptions, field, row]);
  const [gtType, setGtType] = useState(defaultGtType);
  const renderGtTypeSelect = (idPrefix) => (
    gtTypeOptions.length > 1 ? (
      <div className="clw-cell-detail-type-row">
        <label className="clw-cell-detail-type-lbl" htmlFor={`${idPrefix}-${rowId}-${field}`}>Audience type</label>
        <select
          id={`${idPrefix}-${rowId}-${field}`}
          className="clw-cell-detail-type-select"
          value={gtType || ''}
          onChange={(e) => setGtType(e.target.value)}
        >
          {gtTypeOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    ) : null
  );

  // The debounce itself lives in the shared useDebouncedValue hook — every
  // typeahead in the app runs the same trailing-edge timer. The raw draft
  // still flips the picker to 'loading' instantly (keystroke feedback); only
  // the FETCH waits for the debounced value to settle.
  const debouncedAdd = useDebouncedValue(addDraft, LOOKUP_DEBOUNCE_MS);
  const debouncedAddRef = useRef(debouncedAdd);
  debouncedAddRef.current = debouncedAdd;
  // Retyping the term the debounce already settled on (Clear then the same
  // term, or "abcd" backspaced to "abc" inside the window) never changes
  // debouncedAdd, so the fetch effect wouldn't re-run and the picker would sit
  // on "Searching…". This tick re-fires it for exactly that case.
  const [addRefetch, setAddRefetch] = useState(0);
  useEffect(() => {
    if (!lookupKind) return undefined;
    const term = addDraft.trim();
    if (!term) { setLookupState({ status: 'idle', rows: [], error: null }); return undefined; }
    setLookupState((s) => ({ ...s, status: 'loading', error: null }));
    if (term === debouncedAddRef.current.trim()) setAddRefetch((t) => t + 1);
    return undefined;
  }, [addDraft, lookupKind]);
  useEffect(() => {
    if (!lookupKind) return undefined;
    const term = debouncedAdd.trim();
    if (!term) return undefined;
    const seq = lookupSeq.current + 1;
    lookupSeq.current = seq;
    let cancelled = false;
    (async () => {
      try {
        const rows = await resolverRef.current(lookupKind, term, {
          rowId, field, row: rowRef.current, types: gtType ? [gtType] : undefined,
        });
        if (cancelled || lookupSeq.current !== seq) return;
        setLookupState({ status: 'done', rows: Array.isArray(rows) ? rows : [], error: null });
      } catch (e) {
        if (cancelled || lookupSeq.current !== seq) return;
        setLookupState({ status: 'done', rows: [], error: e?.message || 'Lookup failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedAdd, lookupKind, rowId, field, retryTick, gtType, addRefetch]);

  // The replace-picker runs the same debounced search against the same
  // resolver, staged for a different commit path (edit, not add).
  const replaceKind = editViaLookup ? cfg.lookup.kind : null;
  const debouncedReplace = useDebouncedValue(replaceDraft, LOOKUP_DEBOUNCE_MS);
  useEffect(() => {
    if (!replaceKind || replacingItem == null) return undefined;
    const term = replaceDraft.trim();
    if (!term) { setReplaceState({ status: 'idle', rows: [], error: null }); return undefined; }
    setReplaceState((s) => ({ ...s, status: 'loading', error: null }));
    return undefined;
  }, [replaceDraft, replaceKind, replacingItem]);
  useEffect(() => {
    if (!replaceKind || replacingItem == null) return undefined;
    const term = debouncedReplace.trim();
    if (!term) return undefined;
    const seq = lookupSeq.current + 1;
    lookupSeq.current = seq;
    let cancelled = false;
    (async () => {
      try {
        const rows = await resolverRef.current(replaceKind, term, {
          rowId, field, row: rowRef.current, types: gtType ? [gtType] : undefined,
        });
        if (cancelled || lookupSeq.current !== seq) return;
        setReplaceState({ status: 'done', rows: Array.isArray(rows) ? rows : [], error: null });
      } catch (e) {
        if (cancelled || lookupSeq.current !== seq) return;
        setReplaceState({ status: 'done', rows: [], error: e?.message || 'Lookup failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedReplace, replaceKind, replacingItem, rowId, field, retryTick, gtType]);

  const commitItemEdit = (item) => {
    onEditListItem?.(rowId, field, item, editDraft.trim());
    setEditingItem(null);
    setEditDraft('');
  };
  const commitAdd = () => {
    const v = addDraft.trim();
    if (!v) return;
    onAddListItem?.(rowId, field, v);
    setAddDraft('');
  };
  // Picking a result stages `<id>|<label>` so the applier can build a resolved
  // entity and the label still round-trips through the row builder. GT
  // taxonomy rows add their `type` too — the composite key needs both.
  const pickValue = (rowResult) => {
    const id = rowResult?.id ?? rowResult?.geoSegmentId ?? rowResult?.thirdPartyDataId;
    const label = rowResult?.label ?? rowResult?.name ?? rowResult?.fullPath;
    if (!id || !label) return null;
    return rowResult?.type
      ? encodeTypedLookupValue(id, rowResult.type, label)
      : encodeLookupValue(id, label);
  };
  // The multi-value GT audiences cell keeps its results open after a pick so
  // several audiences can be staged from one search; a pick toggles (the hook
  // un-stages a value added twice). Single-select cells and the other
  // platforms' pickers keep pick-and-close.
  const multiPick = lookupKind === 'gt-taxonomy' && !single;
  const clearLookupSearch = () => {
    // Drop any in-flight query too, so a late response can't repopulate.
    lookupSeq.current += 1;
    setAddDraft('');
    setLookupState({ status: 'idle', rows: [], error: null });
  };
  const commitLookupPick = (rowResult) => {
    const value = pickValue(rowResult);
    if (!value) return;
    if (multiPick) {
      const { state, stagedValue } = lookupPickState(rowResult);
      if (state === 'applied') return;
      // Re-picking a staged record passes the STAGED value back, so the
      // hook's toggle finds it even if this result's label differs.
      onAddListItem?.(rowId, field, state === 'staged' ? stagedValue : value);
      return;
    }
    onAddListItem?.(rowId, field, value);
    clearLookupSearch();
  };
  const commitReplacePick = (rowResult) => {
    const id = rowResult?.id ?? rowResult?.geoSegmentId ?? rowResult?.thirdPartyDataId;
    const label = rowResult?.label ?? rowResult?.name ?? rowResult?.fullPath;
    if (!id || !label || replacingItem == null) return;
    const value = rowResult?.type
      ? encodeTypedLookupValue(id, rowResult.type, label)
      : encodeLookupValue(id, label);
    onEditListItem?.(rowId, field, replacingItem, value);
    setReplacingItem(null);
    setReplaceDraft('');
    setReplaceState({ status: 'idle', rows: [], error: null });
  };
  // Picker rows show name + id + type; the label stored in the plan stays the
  // record's real name (the id and type are what launch).
  const optionLabel = (rowResult) => {
    const id = rowResult?.id ?? rowResult?.geoSegmentId ?? rowResult?.thirdPartyDataId;
    const label = rowResult?.label ?? rowResult?.name ?? rowResult?.fullPath;
    return rowResult?.type ? `${label} · ${id} · ${rowResult.type}` : label;
  };
  // Multi-pick only: 'staged' (picked this session — click again to un-pick),
  // 'applied' (already on the ad group), or null. Matched on (id, type), the
  // same identity applyGtAudienceListOps de-dupes on, so a record the applier
  // would drop as a duplicate never reads as a fresh pick. Existing items
  // render as `name · id · type` (gtAudienceLabel) — id and type are the last
  // two segments even when the name itself contains ' · '.
  function lookupPickState(rowResult) {
    if (!multiPick) return { state: null };
    const id = String(rowResult?.id ?? '').trim();
    const type = String(rowResult?.type ?? '').trim();
    if (!id || !type) return { state: null };
    const stagedValue = stagedAdds.find((v) => {
      const d = decodeLookupValue(v);
      return d.id === id && d.type === type;
    });
    if (stagedValue) return { state: 'staged', stagedValue };
    const applied = items.some((it) => {
      if (pendingRemoved?.has(it)) return false;
      const parts = String(it).split(' · ');
      return parts.length >= 3
        && parts[parts.length - 2].trim() === id && parts[parts.length - 1].trim() === type;
    });
    return { state: applied ? 'applied' : null };
  }

  const renderLookupResults = (state, commitFn) => (
    <>
      {state.status === 'loading' && (
        <div className="clw-cell-detail-list-note">Searching…</div>
      )}
      {state.status === 'done' && state.error && (
        <div className="clw-cell-detail-list-note">{friendlyLookupError(state.error)}</div>
      )}
      {/* The GT picker's recovery path: whenever the search came back with
          NOTHING to show (the no-cache 404, any other error, or simply no
          matches), offer the live fetch — an un-launched account's catalog is
          only as fresh as its last cache write. On success the search re-runs. */}
      {state.status === 'done' && state.rows.length === 0 && lookupRefreshable && (
        <div className="clw-cell-detail-refresh-row">
          <button
            type="button"
            className="clw-btn clw-btn-secondary clw-btn-sm"
            onClick={runLookupRefresh}
            disabled={refreshState.busy}
          >
            {refreshState.busy ? 'Fetching from GT…' : 'Fetch GT taxonomy'}
          </button>
          {refreshState.error && (
            <div className="clw-cell-detail-list-note">{refreshState.error}</div>
          )}
        </div>
      )}
      {state.status === 'done' && !state.error && state.rows.length === 0 && (
        <div className="clw-cell-detail-list-note">No matches</div>
      )}
      {state.rows.map((rowResult) => {
        const pickState = commitFn === commitLookupPick ? lookupPickState(rowResult).state : null;
        return (
          <button
            key={`${rowResult.id ?? rowResult.geoSegmentId ?? rowResult.thirdPartyDataId}${rowResult.type ? `-${rowResult.type}` : ''}`}
            type="button"
            role="option"
            aria-selected={pickState != null}
            aria-disabled={pickState === 'applied' || undefined}
            className={`clw-cell-detail-lookup-option${pickState ? ` is-${pickState}` : ''}`}
            title={pickState === 'applied' ? 'Already on this ad group'
              : pickState === 'staged' ? 'Selected — click to unselect' : undefined}
            // Commit on mousedown: the input's blur would otherwise close
            // the dropdown before the click landed.
            onMouseDown={(e) => { e.preventDefault(); commitFn(rowResult); }}
          >
            {pickState && <span aria-hidden="true">✓ </span>}
            {optionLabel(rowResult)}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="clw-cell-detail-val-list">
      {items.map((item, i) => {
        const pending = pendingRemoved?.has(item);
        const stagedEdit = editByValue.get(item);
        const isEditingThis = canEdit && !editViaLookup && editingItem === item;
        const isReplacingThis = editViaLookup && replacingItem === item;
        return (
          <div key={i} className={`clw-cell-detail-val-list-item${pending ? ' pending-delete' : ''}`}>
            {isEditingThis ? (
              <input
                autoFocus
                type="text"
                className="clw-cell-detail-list-edit-input"
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitItemEdit(item); }
                  else if (e.key === 'Escape') { e.preventDefault(); setEditingItem(null); setEditDraft(''); }
                }}
                onBlur={() => commitItemEdit(item)}
                autoComplete="off"
                spellCheck={false}
              />
            ) : (
              <span style={{ flex: 1 }}>
                · {row?.listDisplayLabels?.[field]?.[item] || item}
                {stagedEdit != null && !pending && (
                  <span className="clw-cell-detail-list-edit-preview">
                    {' '}→ {lookup ? lookupValueLabel(stagedEdit) : stagedEdit}
                  </span>
                )}
              </span>
            )}
            {canEdit && !pending && !isEditingThis && (
              <button
                type="button"
                className="clw-cell-detail-del-btn clw-cell-detail-item-edit-btn"
                onClick={() => {
                  if (editViaLookup) {
                    setReplacingItem(item);
                    setReplaceDraft('');
                    setReplaceState({ status: 'idle', rows: [], error: null });
                  } else {
                    setEditingItem(item);
                    setEditDraft(stagedEdit != null ? stagedEdit : item);
                  }
                }}
                title="Edit this entry"
              >
                ✎
              </button>
            )}
            {isReplacingThis && (
              <span className="clw-cell-detail-list-note">Replacing {item}…</span>
            )}
            {removable && !isEditingThis && (
              <button
                type="button"
                className="clw-cell-detail-del-btn"
                onClick={() => onToggleListDeletion?.(rowId, field, item)}
                title={pending ? 'Undo delete' : removeTitle}
              >
                {pending ? '↩' : '×'}
              </button>
            )}
          </div>
        );
      })}
      {/* Replace picker for lookup-backed edits — the only edit mode for
          id-backed fields, because a relabel would change the label while
          the id stayed. */}
      {editViaLookup && replacingItem != null && (
        <>
          {renderGtTypeSelect('gt-replace-type')}
          <div className="clw-cell-detail-add-row">
            <input
              autoFocus
              type="text"
              className="clw-cell-detail-list-edit-input"
              placeholder="Search replacement…"
              value={replaceDraft}
              onChange={(e) => setReplaceDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (replaceState.rows.length === 1) commitReplacePick(replaceState.rows[0]);
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </>
      )}
      {editViaLookup && replacingItem != null && replaceDraft.trim()
        && renderLookupResults(replaceState, commitReplacePick)}
      {/* Staged adds — appended with their own × to un-stage (the hook
          toggles the add op off when the same value is added again). */}
      {stagedAdds.map((v, i) => (
        <div key={`add-${i}`} className="clw-cell-detail-val-list-item pending-add">
          {/* Lookup-picked values are `<id>|<label>` — show the label half. */}
          <span style={{ flex: 1 }}>· {lookup ? lookupValueLabel(v) : v}</span>
          <button
            type="button"
            className="clw-cell-detail-del-btn"
            onClick={() => onAddListItem?.(rowId, field, v)}
            title="Remove this staged addition"
          >
            ×
          </button>
        </div>
      ))}
      {canAdd && (
        <>
          {renderGtTypeSelect('gt-add-type')}
          <div className="clw-cell-detail-add-row">
            <input
              type="text"
              className="clw-cell-detail-list-edit-input"
              placeholder={cfg?.addPlaceholder || 'Add…'}
              value={addDraft}
              onChange={(e) => setAddDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                // With a lookup there is no free-text value to stage: Enter
                // takes the single result when the search narrowed to one,
                // otherwise the user has to pick.
                if (!lookup) { commitAdd(); return; }
                if (lookupState.rows.length === 1) commitLookupPick(lookupState.rows[0]);
              }}
              autoComplete="off"
              spellCheck={false}
            />
            {multiPick && addDraft && (
              <button
                type="button"
                className="clw-btn clw-btn-secondary clw-btn-sm"
                onClick={clearLookupSearch}
                title="Clear search results"
              >
                Clear
              </button>
            )}
            {!lookup && (
              <button
                type="button"
                className="clw-btn clw-btn-secondary clw-btn-sm"
                onClick={commitAdd}
                disabled={!addDraft.trim()}
              >
                Add
              </button>
            )}
          </div>
          {lookup && addDraft.trim() && renderLookupResults(lookupState, commitLookupPick)}
          {cfg?.note && <div className="clw-cell-detail-list-note">{cfg.note}</div>}
        </>
      )}
      {!canAdd && cfg?.reason && (
        <div className="clw-cell-detail-list-note">{cfg.reason}</div>
      )}
    </div>
  );
}

// Columns that are ALWAYS list-shaped, on every platform. Anything else
// becomes a list when the platform's listFields config grants ops on it.
const CORE_LIST_FIELDS = ['audiences', 'keywords', 'interests', 'pmp'];

// Fields whose × (remove) control renders when the host passes NO config for
// them — the behaviour from before per-platform list configs existed.
const LEGACY_REMOVABLE_FIELDS = new Set([
  'audiences', 'keywords', 'interests', 'geoInclude', 'geoExclude',
]);

/**
 * Whether a list field's × (remove) control should render.
 *
 * An explicit per-platform config is AUTHORITATIVE. This used to be ignored
 * for audiences / keywords / interests / geo, which rendered × unconditionally
 * — so a platform that grants no ops still showed delete buttons that silently
 * did nothing. TTD copy templates are exactly that case: their targeting is
 * carried by reference from the source campaign's bid lists, the applier has
 * no TTD list branch, and the save is skipped as unchanged, so the row just
 * re-rendered intact with no feedback.
 *
 * Fields the host doesn't configure keep the legacy default, and every CLW
 * config grants `remove: true` explicitly, so the launch flow is unaffected.
 */
function isRemoveGranted(listFields, field, isGt = false) {
  const ops = listFields?.[field]?.ops;
  if (ops && typeof ops.remove === 'boolean') return ops.remove;
  // GT has no legacy implicit-removable fields: every capability comes from
  // the descriptor. Other platforms keep the pre-config default for hosts
  // that haven't adopted explicit list configs yet.
  if (isGt) return false;
  return LEGACY_REMOVABLE_FIELDS.has(field);
}

// Grid field → the row key carrying its full ' · '-joined entries (the grid
// cell itself shows a count summary). `interests` is handled separately: its
// entries ride an array (interestIds), not a joined string.
const LIST_CELL_VALUE_KEY = {
  audiences: 'audienceList',
  keywords: 'keywordList',
  pmp: 'pmpDealList',
  headlines: 'headlineList',
  descriptions: 'descriptionList',
  negativeKeywords: 'negativeKeywordList',
  zipcodes: 'zipcodeList',
  sitelinks: 'sitelinkList',
  creative: 'creativeList',
  videos: 'videoList',
};

// ─── CELL DETAIL PANEL ───────────────────────────────────────
// Renders the clicked cell's column label + value as the headline, then
// validation context (if any), row context (ad_group_name + channel +
// group), and an expandable "Show all fields for this row" section that
// reveals the full grouped dump the prior implementation always showed.
export function CellDetailPanel({
  detailRow, detailCellField, isGt, groupCols, groupKeys, getRowValue,
  ctsReview, pendingDeletionsForRow, onToggleListDeletion, onClose,
  alreadyLaunched = false, editableFields, fieldEditors, fieldOptions = null,
  readOnlyReasons = null,
  editingCell, setEditingCell, editingDraft, setEditingDraft,
  commitCellEdit, cancelCellEdit, editedRows,
  // Per-platform list-field capabilities (TTD_LIST_FIELDS / DV360_LIST_FIELDS
  // / {}) plus the staged list-ops for this row and the stage callbacks.
  // All optional — hosts that only wire the legacy remove flow (or test
  // fixtures that pass none of them) keep the remove-only behaviour.
  listFields = {}, pendingListOpsForRow = null,
  onAddListItem, onEditListItem,
  // Async resolver for lookup-backed adds: (kind, query) => Promise<rows>.
  // Supplied by the host (it owns the campaign id + api client). Absent ⇒
  // fields configured with a `lookup` descriptor stay non-addable.
  onLookup,
  // Optional recovery for GT taxonomy lookups whose account has no cache yet:
  // () => Promise — the host asks app-backend to have campaign-services fetch
  // the taxonomy live and cache it to GCS. Rendered only on the no-cache error.
  onRefreshLookup,
}) {
  const [expandRow, setExpandRow] = useState(false);
  // Cells filled from a COPY's targeting_plan sidecar are DISPLAY-ONLY. The edit
  // appliers understand only the normalized li.geoTargeting / li.audiences shape a
  // copy does not have, so an edit would create that key alongside the sidecar
  // entry (the launcher applies one at create and the other after — the line item
  // ends up targeting both) and a remove would silently do nothing. Strip the ops
  // and state the reason; buildDv360ReviewRows marks which fields are affected.
  const planLockedFields = useMemo(
    () => new Set(detailRow?.targetingFromPlanFields || []),
    [detailRow],
  );
  //
  // REMOVE is allowed and lands in the sidecar (applyDv360PlanListOps prunes
  // targeting_plan). ADD is geo-only: a market is staged into geo_plan as a NAME
  // and resolved at launch, whereas an audience or keyword add would need a DV360
  // option-id lookup the grid cannot perform. EDIT stays off everywhere — it is a
  // remove plus an add, so it is only meaningful where add is, and doing it as two
  // explicit steps keeps what lands in the sidecar obvious.
  const lockPlanField = (field, cfg) => {
    if (!cfg || !planLockedFields.has(field)) return cfg;
    const geoAddable = field === 'geoInclude' || field === 'geoExclude';
    return {
      ...cfg,
      ops: { add: geoAddable && Boolean(cfg.ops?.add), edit: false, remove: true },
      reason: geoAddable
        ? 'Carried from the source campaign. Markets you add here are resolved when the copy launches.'
        : "Carried from the source campaign and applied after the line items are created. You can remove entries here; adding one needs a DV360 lookup the grid can't do.",
    };
  };
  // Locate the column metadata for the clicked field, walking every group
  // so we can render the column label + fmt regardless of which tab is
  // active. Falls back to a derived label when the click was on the
  // sticky `name` column (not in any group).
  const colMeta = useMemo(() => {
    if (!detailRow || !detailCellField) return null;
    if (detailCellField === 'name') return { k: 'name', l: 'Ad group', fmt: undefined };
    for (const g of Object.values(groupCols || {})) {
      const m = (g || []).find((c) => c.k === detailCellField);
      if (m) return m;
    }
    return { k: detailCellField, l: detailCellField, fmt: undefined };
  }, [detailRow, detailCellField, groupCols]);

  const groupContainingField = useMemo(() => {
    if (!detailCellField) return null;
    for (const [name, cols] of Object.entries(groupCols || {})) {
      if ((cols || []).some((c) => c.k === detailCellField)) return name;
    }
    return null;
  }, [detailCellField, groupCols]);

  if (!detailRow) {
    return <div className="clw-dpanel" />;
  }
  const rawValue = colMeta ? getRowValue(detailRow, colMeta.k) : null;
  const formatted = fmtCellValue(rawValue, colMeta?.fmt, detailRow?.currencyCode, detailRow);
  const annotation = colMeta ? (detailRow.annotations || {})[colMeta.k] : null;
  // Detect whether the value should be rendered in mono font (numbers,
  // currency, dates, IDs, sizes — anything that's parsed/displayed as
  // numeric or codey text).
  const isMonoVal = (
    typeof rawValue === 'number'
    || /^[\d.,$%-]+$/.test(String(formatted || ''))
    || /^\d/.test(String(formatted || ''))
  );

  return (
    <div className={`clw-dpanel ${detailRow ? 'open' : ''}`}>
      <div className="clw-dp-head">
        <button className="clw-dp-close" onClick={onClose}>✕</button>
        <div className="clw-dp-meta">
          {(detailRow.channel || (isGt ? 'GroundTruth' : 'DV360'))}
          {groupContainingField ? <> · {groupContainingField}</> : null}
        </div>
        <div className="clw-dp-title">Cell detail</div>
      </div>
      <div className="clw-dp-body">
        {/* Cell hero — column label + value. For · -separated list fields
            (geo, audiences, keywords, interests, etc.) show each item as
            a compact bulleted row instead of one giant 32px text block.
            For audiences/keywords/interests the hero uses the full name
            list rather than the count summary shown in the grid cell. */}
        {(() => {
          // A value that could not be read at all (an `unread` annotation):
          // no value, list or editor — "—" or an empty list would read as
          // "there is none". The annotation below says what happened.
          if (annotation?.unread) {
            return (
              <div className="clw-cell-detail-hero">
                <div className="clw-cell-detail-col">{colMeta?.l || detailCellField}</div>
                <div className="clw-cell-detail-val" data-testid="clw-cell-detail-unread" />
              </div>
            );
          }
          // Geo cell renders both include and exclude lists as separate
          // sections so reviewers can see what's targeted AND what's blocked.
          if (detailCellField === 'geo') {
            const incRaw = detailRow.geoIncludeList && detailRow.geoIncludeList !== '—' ? detailRow.geoIncludeList : null;
            const excRaw = detailRow.geoExcludeList && detailRow.geoExcludeList !== '—' ? detailRow.geoExcludeList : null;
            const incItems = incRaw ? incRaw.split(' · ') : [];
            const excItems = excRaw ? excRaw.split(' · ') : [];
            // Pending-delete sets for the two sub-fields. DV360 has clean
            // per-LI ownership of geoTargeting; TTD shares geographyId
            // across ad groups via CommonGeoLists, so deletes there
            // edit the shared list (same intent — "drop this market").
            const geoEditable = !alreadyLaunched;
            // Honor the platform's remove grant (see isRemoveGranted) instead
            // of showing × on every geo entry regardless of capability.
            const incRemovable = geoEditable && isRemoveGranted(listFields, 'geoInclude', isGt);
            const excRemovable = geoEditable && isRemoveGranted(listFields, 'geoExclude', isGt);
            const pendingInc = geoEditable ? new Set(pendingDeletionsForRow?.geoInclude || []) : null;
            const pendingExc = geoEditable ? new Set(pendingDeletionsForRow?.geoExclude || []) : null;
            // A COPY's geo comes from the targeting_plan sidecar, and the edit
            // appliers only understand the normalized li.geoTargeting key. An edit
            // would CREATE that key while the sidecar entry stays — the launcher
            // applies geoTargeting at create and the sidecar after, so the line
            // item would target BOTH — and a remove is a silent no-op because
            // applyDv360Deletions bails on the missing key. Show the markets,
            // offer no ops, say why.
            const incCfg = lockPlanField('geoInclude', geoEditable ? listFields?.geoInclude : null);
            const excCfg = lockPlanField('geoExclude', geoEditable ? listFields?.geoExclude : null);
            // With a list config granting `add`, the section renders even
            // when currently empty so the add row has somewhere to live.
            const showInc = incItems.length > 0 || Boolean(incCfg?.ops?.add);
            const showExc = excItems.length > 0 || Boolean(excCfg?.ops?.add);
            return (
              <div className="clw-cell-detail-hero">
                <div className="clw-cell-detail-col">{colMeta?.l || detailCellField}</div>
                {showInc && (
                  <>
                    <div className="clw-cell-detail-section-lbl">Included</div>
                    <div className="clw-cell-detail-val mono" style={{ fontSize: 22, marginBottom: 10 }}>{incItems.length}</div>
                    <EditableList
                      rowId={detailRow.rowId}
                      field="geoInclude"
                      items={incItems}
                      // Reason renders once under both sections (below), not
                      // per-section — strip it from the list's own cfg.
                      cfg={incCfg ? { ...incCfg, reason: null } : null}
                      canRemove={incRemovable}
                      pendingRemoved={pendingInc}
                      pendingOps={geoEditable ? pendingListOpsForRow?.geoInclude : null}
                      onToggleListDeletion={onToggleListDeletion}
                      onAddListItem={onAddListItem}
                      onEditListItem={onEditListItem}
                      onLookup={onLookup}
                      onRefreshLookup={onRefreshLookup}
                      removeTitle="Drop this included market"
                    />
                  </>
                )}
                {showExc && (
                  <>
                    <div className="clw-cell-detail-section-lbl" style={{ marginTop: 16 }}>Excluded</div>
                    <div className="clw-cell-detail-val mono" style={{ fontSize: 22, marginBottom: 10 }}>{excItems.length}</div>
                    <EditableList
                      rowId={detailRow.rowId}
                      field="geoExclude"
                      items={excItems}
                      cfg={excCfg ? { ...excCfg, reason: null } : null}
                      canRemove={excRemovable}
                      pendingRemoved={pendingExc}
                      pendingOps={geoEditable ? pendingListOpsForRow?.geoExclude : null}
                      onToggleListDeletion={onToggleListDeletion}
                      onAddListItem={onAddListItem}
                      onEditListItem={onEditListItem}
                      onLookup={onLookup}
                      onRefreshLookup={onRefreshLookup}
                      removeTitle="Drop this excluded market"
                    />
                  </>
                )}
                {/* Add-disabled reason for geo (TTD) — shown once under the
                    lists rather than per-section. */}
                {geoEditable && incCfg && !incCfg.ops?.add && incCfg.reason && (
                  <div className="clw-cell-detail-list-note">{incCfg.reason}</div>
                )}
                {!showInc && !showExc && (
                  <div className="clw-cell-detail-val">—</div>
                )}
              </div>
            );
          }
          // A cell is list-shaped when it is one of the always-list columns or
          // when the platform's list config grants ops on it (Google Ads adds
          // copy / sitelinks / zip codes / images / videos that way).
          const LIST_FIELDS = new Set([...CORE_LIST_FIELDS, ...Object.keys(listFields || {})]);
          const listsEditable = !alreadyLaunched;
          // lockPlanField covers audiences / interests / keywords here; the geo
          // branch above locks its own two sub-fields.
          const rawFieldCfg = LIST_FIELDS.has(detailCellField)
            ? lockPlanField(detailCellField, listFields?.[detailCellField] || null) : null;
          // Product scoping (GT audiences is hidden on On Premise — R14).
          // The cell stays list-shaped with every capability switched off and
          // a written reason, rather than falling back to a scalar editor.
          const productToken = String(detailRow?.product || '').trim().toLowerCase()
            .replace('on premise', 'onpremise');
          let fieldCfg = listsEditable ? rawFieldCfg : null;
          if (fieldCfg?.appliesToProducts?.length
              && !fieldCfg.appliesToProducts.includes(productToken)) {
            fieldCfg = {
              ops: { add: false, edit: false, remove: false },
              reason: fieldCfg.productReason || 'This field does not apply to this ad group\'s product',
              ...(fieldCfg.single ? { single: true } : {}),
            };
          }
          let heroVal = formatted;
          // The grid cell of a list-shaped column shows a COUNT summary ("3
          // headlines"); the panel expands the row's companion list key so
          // reviewers see — and can edit — the actual entries. (PMP, for
          // instance, shows "PMP · 9 deals" in the grid and every
          // SupplyVendorDealId here; the inventory label + count render
          // separately above the deal list.)
          const listValueKey = LIST_FIELDS.has(detailCellField)
            ? LIST_CELL_VALUE_KEY[detailCellField] : null;
          if (detailCellField === 'interests' && detailRow.interestIds?.length) heroVal = detailRow.interestIds.join(' · ');
          else if (listValueKey && detailRow[listValueKey] && detailRow[listValueKey] !== '—') {
            heroVal = detailRow[listValueKey];
          }
          let items = heroVal && heroVal !== '—'
            ? (heroVal.includes(' · ') ? heroVal.split(' · ') : (LIST_FIELDS.has(detailCellField) ? [heroVal] : null))
            : null;
          // GT audiences: each entry's label itself contains ' · '
          // (name · id · type), so splitting the joined audienceList broke ONE
          // audience into THREE list rows (UAT 0817). The row builder ships
          // the unambiguous entry array — use it verbatim; the third segment
          // is the audience type the reviewers asked to see per entry.
          if (detailCellField === 'audiences' && Array.isArray(detailRow.audienceEntries)) {
            items = detailRow.audienceEntries;
          }
          // A single-select list cell renders as a 0/1-item list so its add /
          // replace / remove controls have somewhere to live.
          if (fieldCfg?.single) items = heroVal && heroVal !== '—' ? [heroVal] : [];
          // An empty list on a field whose config allows adding (or that is
          // product-scoped off) still renders as a list so the add row — or
          // the written reason — has somewhere to live.
          if (!items && (fieldCfg?.ops?.add || fieldCfg?.single || fieldCfg?.reason)) items = [];
          // A SCALAR field whose value merely CONTAINS ' · ' was being split
          // into a list above — DV360 `pacing` ("AHEAD · DAILY"), `convWindow`
          // ("30d click · 7d view"), `bidStrategy`, `kpi`, `freqCap`, and the
          // Google Ads ' · '-joined copy fields all hit this. With no list
          // config the split produced a DEAD list: EditableList got cfg=null so
          // it rendered no add/edit/remove controls, and because `items` was
          // truthy the scalar editor never rendered either. The field was
          // marked editable by the platform and yet had no way to be edited —
          // the single largest cause of "most cells aren't editable".
          //
          // Only genuinely list-shaped fields stay lists. Multi-value cells
          // that are NOT scalar-editable (DV360 devices / environment /
          // viewability / brandSafety) keep today's readable bulleted layout.
          if (items && !LIST_FIELDS.has(detailCellField)
              && !alreadyLaunched && editableFields?.has(detailCellField)) {
            items = null;
          }
          const isPmpField = detailCellField === 'pmp';
          const pmpInventory = isPmpField ? detailRow.pmpInventory : null;
          // Per-item delete (DV360/TTD only) on audiences / keywords /
          // interests cells — lets reviewers prune the assigned list
          // without leaving the panel. Pending deletions strike through
          // the entry; clicking the same X again undoes the pending delete.
          // GT cells don't pipe through the launcher_result that backs
          // this edit, so we skip the delete affordance there. pmp joins
          // via the platform list config's remove grant.
          const deletableField = listsEditable && isRemoveGranted(listFields, detailCellField, isGt)
            ? detailCellField : null;
          const pendingForField = deletableField ? new Set(pendingDeletionsForRow?.[deletableField] || []) : null;
          const removeNoun = detailCellField === 'audiences' ? 'audience'
            : detailCellField === 'keywords' ? 'keyword'
            : detailCellField === 'interests' ? 'interest'
            : 'entry';
          // Audience freshness is handled by the on-load bulk refresh (banner
          // above the grid), so the cell panel just renders the current list.
          return (
            <div className="clw-cell-detail-hero">
              <div className="clw-cell-detail-col">{colMeta?.l || detailCellField}</div>
              {/* Inventory line (PMP / Open exchange) — only for the pmp cell. */}
              {isPmpField && pmpInventory && pmpInventory !== '—' && (
                <div className="clw-cell-detail-section-lbl" style={{ marginBottom: 8 }}>Inventory · {pmpInventory}</div>
              )}
              {items ? (
                <>
                  <div className="clw-cell-detail-val mono" style={{ fontSize: 22, marginBottom: 10 }}>{items.length}{isPmpField ? ` deal${items.length === 1 ? '' : 's'}` : ''}</div>
                  <EditableList
                    rowId={detailRow.rowId}
                    field={detailCellField}
                    items={items}
                    cfg={fieldCfg}
                    canRemove={Boolean(deletableField)}
                    pendingRemoved={pendingForField}
                    pendingOps={listsEditable ? pendingListOpsForRow?.[detailCellField] : null}
                    onToggleListDeletion={onToggleListDeletion}
                    onAddListItem={onAddListItem}
                    onEditListItem={onEditListItem}
                    onLookup={onLookup}
                    onRefreshLookup={onRefreshLookup}
                    row={detailRow}
                    removeTitle={`Delete this ${removeNoun}`}
                  />
                </>
              ) : (
                <CellScalarEditor
                  detailRow={detailRow}
                  detailCellField={detailCellField}
                  heroVal={heroVal}
                  rawValue={rawValue}
                  isMonoVal={isMonoVal}
                  alreadyLaunched={alreadyLaunched}
                  editableFields={editableFields}
                  fieldEditors={fieldEditors}
                  fieldOptions={fieldOptions}
                  readOnlyReasons={readOnlyReasons}
                  onLookup={onLookup}
                  editingCell={editingCell}
                  setEditingCell={setEditingCell}
                  editingDraft={editingDraft}
                  setEditingDraft={setEditingDraft}
                  commitCellEdit={commitCellEdit}
                  cancelCellEdit={cancelCellEdit}
                  editedRows={editedRows}
                />
              )}
            </div>
          );
        })()}

        {/* Annotation on this specific cell, if any. */}
        {annotation && (
          <div className={`clw-pf-${annotation.type}`} style={{ marginBottom: 16 }}>
            <div className="clw-pf-ann-type">
              {annotation.type === 'm' ? '! Error' : annotation.type === 'q' ? '? Warning' : 'ⓘ Info'}
              {annotation.category ? ` — ${annotation.category}` : ''}
            </div>
            <div className="clw-pf-ann-msg">{annotation.msg}</div>
          </div>
        )}

        {/* Row context — which ad group this cell lives on. */}
        <div className="clw-cell-detail-ctx">
          <div className="clw-cell-detail-ctx-row">
            <div className="clw-cell-detail-ctx-lbl">Ad group</div>
            <div className="clw-cell-detail-ctx-val">{getRowValue(detailRow, 'name') || '—'}</div>
          </div>
          {isGt && ctsReview?.result?.campaign?.io_name && (
            <div className="clw-cell-detail-ctx-row">
              <div className="clw-cell-detail-ctx-lbl">IO</div>
              <div className="clw-cell-detail-ctx-val">{ctsReview.result.campaign.io_name}</div>
            </div>
          )}
          <div className="clw-cell-detail-ctx-row">
            <div className="clw-cell-detail-ctx-lbl">Field key</div>
            <div className="clw-cell-detail-ctx-val" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{detailCellField}</div>
          </div>
        </div>

        {/* Optional expand to full-row dump (the old behaviour, gated). */}
        <button
          className={`clw-cell-detail-expander${expandRow ? ' expanded' : ''}`}
          onClick={() => setExpandRow((v) => !v)}
          type="button"
        >
          <span>{expandRow ? '▾' : '▸'}</span>
          <span>Show all fields for this ad group</span>
        </button>
        {expandRow && groupKeys.map((g) => (
          <div key={g} style={{ marginTop: 12 }}>
            <div className="clw-ps-sect">{g.charAt(0).toUpperCase() + g.slice(1)}</div>
            {(groupCols[g] || []).map((c) => (
              <div className="clw-pf" key={c.k}>
                <div className="clw-pfl">{c.l}</div>
                <div className="clw-pfv">
                  {detailRow.annotations?.[c.k]?.unread
                    ? <span className="clw-pf-unread">Not read</span>
                    : fmtCellValue(getRowValue(detailRow, c.k), c.fmt, detailRow?.currencyCode, detailRow)}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="clw-dp-foot">
        <button className="clw-btn clw-btn-secondary clw-btn-sm" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
